import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styles/styled-components';
import translations from 'translations';
import messages from './SolutionsDropdown.messages';

import iconRestaurant from './icon-restaurant.png';
import iconCoffeeBakery from './icon-coffee-bakery.png';
import iconEatery from './icon-eatery.png';
import iconBarPub from './icon-bar-pub.png';
import iconHotel from './icon-hotel.png';
import iconStore from './icon-store.png';
import iconOffice from './icon-office.png';
import iconSchoolFactory from './icon-school-factory.png';
import iconArrowUpRight from './icon-arrow-up-right.svg';

export interface BusinessType {
  key: string;
  icon: string;
  title: string;
  description: string;
}

export interface FeaturedProduct {
  key: string;
  title: string;
  description: string;
  href: string;
  external?: boolean;
}

export const BUSINESS_TYPE_HREF = '/';

export const useBusinessTypes = (): BusinessType[] => [
  { key: 'restaurant', icon: iconRestaurant, title: translations(messages.restaurantTitle), description: translations(messages.restaurantDescription) },
  { key: 'coffeeBakery', icon: iconCoffeeBakery, title: translations(messages.coffeeBakeryTitle), description: translations(messages.coffeeBakeryDescription) },
  { key: 'eatery', icon: iconEatery, title: translations(messages.eateryTitle), description: translations(messages.eateryDescription) },
  { key: 'barPub', icon: iconBarPub, title: translations(messages.barPubTitle), description: translations(messages.barPubDescription) },
  { key: 'hotel', icon: iconHotel, title: translations(messages.hotelTitle), description: translations(messages.hotelDescription) },
  { key: 'store', icon: iconStore, title: translations(messages.storeTitle), description: translations(messages.storeDescription) },
  { key: 'office', icon: iconOffice, title: translations(messages.officeTitle), description: translations(messages.officeDescription) },
  { key: 'schoolFactory', icon: iconSchoolFactory, title: translations(messages.schoolFactoryTitle), description: translations(messages.schoolFactoryDescription) },
];

export const useFeaturedProducts = (): FeaturedProduct[] => [
  {
    key: 'farmToBusiness',
    title: translations(messages.productFarmToBusinessTitle),
    description: translations(messages.productFarmToBusinessDescription),
    href: '/tu-nong-trai-den-doanh-nghiep',
  },
  {
    key: 'kamereoNgon',
    title: translations(messages.productKamereoNgonTitle),
    description: translations(messages.productKamereoNgonDescription),
    href: '/kamereo-ngon',
    external: true,
  },
  {
    key: 'kamereoTienDung',
    title: translations(messages.productKamereoTienDungTitle),
    description: translations(messages.productKamereoTienDungDescription),
    href: '/kamereo-tien-dung',
    external: true,
  },
];

const HEADER_HEIGHT_PX = 68;
const GAP_FROM_HEADER_PX = 13;
const BRIDGE_HEIGHT_PX = HEADER_HEIGHT_PX + GAP_FROM_HEADER_PX - 47;

const Overlay = styled.div`
  position: fixed;
  top: ${HEADER_HEIGHT_PX}px;
  left: 0;
  width: 100%;
  height: calc(100% - ${HEADER_HEIGHT_PX}px);
  background: rgba(0, 0, 0, 0.3);
  z-index: 105;
  pointer-events: none;
`;

const Bridge = styled.div`
  position: absolute;
  top: 95%;
  left: 0;
  width: 100%;
  height: ${BRIDGE_HEIGHT_PX}px;
  z-index: 110;
`;

const Panel = styled.div`
  position: fixed;
  top: ${HEADER_HEIGHT_PX + GAP_FROM_HEADER_PX}px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 110;
`;

const Card = styled.div`
  display: flex;
  gap: 16px;
  padding: 24px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 2px 8px 40px 0px rgba(31, 31, 31, 0.04);
`;

const BusinessTypesColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 700px;
`;

const SectionLabel = styled.p`
  margin: 0;
  color: #4b5563;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  white-space: nowrap;
`;

const BusinessTypesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  width: 100%;
`;

const BusinessTypeCard = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 56px;
  min-width: 0;
  padding: 8px 12px;
  border-radius: 8px;
  text-decoration: none;
  &:hover {
    background: #f7f7f8;
  }
`;

const BusinessTypeIconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 100px;
  background: #f7f7f8;
`;

const BusinessTypeIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const BusinessTypeTextBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const BusinessTypeTitle = styled.p`
  margin: 0;
  color: #303643;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
`;

const BusinessTypeDescription = styled.p`
  margin: 0;
  overflow: hidden;
  color: #6b7280;
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const Divider = styled.div`
  flex-shrink: 0;
  width: 1px;
  align-self: stretch;
  background: #e5e7eb;
`;

const FeaturedProductsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 260px;
  justify-content: center;
`;

const FeaturedProductLink = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px;
  border-radius: 8px;
  text-decoration: none;
  &:hover {
    background: #f7f7f8;
  }
`;

const FeaturedProductTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const FeaturedProductTitle = styled.p`
  margin: 0;
  color: #303643;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  white-space: nowrap;
`;

const FeaturedProductArrowIcon = styled.img`
  flex-shrink: 0;
  width: 12px;
  height: 12px;
`;

const FeaturedProductDescription = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 12px;
  line-height: 18px;
`;

const SolutionsDropdown: FC = () => {
  const businessTypes = useBusinessTypes();
  const featuredProducts = useFeaturedProducts();

  return (
    <>
      <Overlay />
      <Bridge />
      <Panel>
        <Card>
        <BusinessTypesColumn>
          <SectionLabel>{translations(messages.sectionBusinessTypes)}</SectionLabel>
          <BusinessTypesGrid>
            {businessTypes.map((item) => (
              <BusinessTypeCard key={item.key} to={BUSINESS_TYPE_HREF}>
                <BusinessTypeIconWrapper>
                  <BusinessTypeIcon src={item.icon} alt="" />
                </BusinessTypeIconWrapper>
                <BusinessTypeTextBox>
                  <BusinessTypeTitle>{item.title}</BusinessTypeTitle>
                  <BusinessTypeDescription>{item.description}</BusinessTypeDescription>
                </BusinessTypeTextBox>
              </BusinessTypeCard>
            ))}
          </BusinessTypesGrid>
        </BusinessTypesColumn>
        <Divider />
        <FeaturedProductsColumn>
          <SectionLabel>{translations(messages.sectionFeaturedProducts)}</SectionLabel>
          {featuredProducts.map((product) => (
            <FeaturedProductLink key={product.key} to={product.href}>
              <FeaturedProductTitleRow>
                <FeaturedProductTitle>{product.title}</FeaturedProductTitle>
                {product.external && <FeaturedProductArrowIcon src={iconArrowUpRight} alt="" />}
              </FeaturedProductTitleRow>
              <FeaturedProductDescription>{product.description}</FeaturedProductDescription>
            </FeaturedProductLink>
          ))}
        </FeaturedProductsColumn>
        </Card>
      </Panel>
    </>
  );
};

export default SolutionsDropdown;
